// web/src/theme/fonts.ts
// Font delivery. CORE fonts are imported and force-inlined as data URIs into
// widget.js (see vite.config lib assetsInlineLimit) so they load with zero
// network/CORS/CSP risk on any host page — the always-safe default + fallback.
// ASSET fonts are vendored woff2 served same-origin at /fonts/{key}.woff2 and
// injected on demand (see ensureAssetFont) only for the fonts a brand selects.
import interWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import spaceGroteskWoff2 from "@fontsource-variable/space-grotesk/files/space-grotesk-latin-wght-normal.woff2?url";

export const FONTS_STYLE_ID = "quizkit-fonts";
const ASSET_FONT_STYLE_PREFIX = "quizkit-font-";

// One static face = one woff2 file at a fixed weight/style. Multi-weight static
// families (licensed brand fonts shipped as separate per-weight files) declare a
// `faces` list; variable / single-weight fonts leave it undefined and use the
// single-file /fonts/{key}.woff2 path spanning `weights`.
export interface FontFace {
  weight: number | string; // static weight (e.g. 700) or a variable range ("100 900")
  style?: "normal" | "italic"; // defaults to normal
  file: string; // basename served same-origin at /fonts/{file}
}

export interface FontDef {
  family: string; // CSS font-family name declared by the @font-face
  kind: "core" | "asset";
  weights: string; // variable weight range for the @font-face
  faces?: FontFace[]; // static multi-weight families: one @font-face per entry
}

export const FONT_LIBRARY: Record<string, FontDef> = {
  inter: { family: "Inter Variable", kind: "core", weights: "100 900" },
  "space-grotesk": { family: "Space Grotesk Variable", kind: "core", weights: "300 700" },
  montserrat: { family: "Montserrat Variable", kind: "asset", weights: "100 900" },
  "dm-sans": { family: "DM Sans Variable", kind: "asset", weights: "100 900" },
  "work-sans": { family: "Work Sans Variable", kind: "asset", weights: "100 900" },
  lora: { family: "Lora Variable", kind: "asset", weights: "400 700" },
  // Single-weight display face (not a variable font): one 400 weight only.
  "bebas-neue": { family: "Bebas Neue", kind: "asset", weights: "400" },
  // Manly (licensed brand fonts, static per-weight woff2). Arpona = serif display
  // (headings); Gill Sans = humanist sans (body). The widget uses weights 400/600/700
  // and italic body (testimonial quotes), so we ship exactly those faces.
  arpona: {
    family: "Arpona",
    kind: "asset",
    weights: "400 700",
    faces: [
      { weight: 400, file: "arpona-400.woff2" },
      { weight: 600, file: "arpona-600.woff2" },
      { weight: 700, file: "arpona-700.woff2" },
    ],
  },
  "gill-sans": {
    family: "Gill Sans",
    kind: "asset",
    weights: "400 700",
    faces: [
      { weight: 400, file: "gill-sans-400.woff2" },
      { weight: 400, style: "italic", file: "gill-sans-400-italic.woff2" },
      { weight: 700, file: "gill-sans-700.woff2" },
    ],
  },
};

// Core faces, inlined. Referenced by name from the shadow-scoped tokens.css.
export const fontsCss = `
@font-face {
  font-family: "Inter Variable";
  font-style: normal;
  font-display: swap;
  font-weight: 100 900;
  src: url(${interWoff2}) format("woff2");
}
@font-face {
  font-family: "Space Grotesk Variable";
  font-style: normal;
  font-display: swap;
  font-weight: 300 700;
  src: url(${spaceGroteskWoff2}) format("woff2");
}
`;

/** @font-face CSS for an ASSET font, pointing at the same-origin asset URL.
 * Returns "" for unknown keys, core fonts (already inlined), or empty origin. */
export function assetFontFace(key: string, origin: string): string {
  const def = FONT_LIBRARY[key];
  if (!def || def.kind !== "asset") return "";
  // Defense-in-depth at the CSS sink: parse the origin to a bare scheme://host
  // so a path/`)`/`}` payload can never break out of the url() (callers already
  // normalize, but the @font-face injection is the actual injection point).
  let base: string;
  try {
    const u = new URL(origin);
    if (u.protocol !== "https:" && u.protocol !== "http:") return "";
    base = u.origin;
  } catch {
    return "";
  }
  // Multi-weight static families enumerate their faces; everything else is a single
  // file at /fonts/{key}.woff2 spanning the declared weight range (unchanged).
  const faces = def.faces ?? [{ weight: def.weights, file: `${key}.woff2` }];
  return faces
    .map(
      (f) =>
        `@font-face { font-family: "${def.family}"; font-style: ${f.style ?? "normal"}; ` +
        `font-display: swap; font-weight: ${f.weight}; ` +
        `src: url(${base}/fonts/${f.file}) format("woff2"); }`,
    )
    .join("");
}

/** Inject an asset font's @font-face into <head> once (idempotent by key). */
export function ensureAssetFont(key: string, origin: string): void {
  if (typeof document === "undefined") return;
  const css = assetFontFace(key, origin);
  if (!css) return;
  const id = ASSET_FONT_STYLE_PREFIX + key;
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}
